import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { getActivities, addActivity, uploadActivityFile, type UploadActivityFileRequest } from '@/services/api/activity.service'
import type { AddActivityRequest } from '@/types/api/activity'

/** @param options.enabled set false to skip loading (e.g. the user may not view the activity) */
export function useActivity(recordId: string, options: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient()

  const query = useInfiniteQuery({
    queryKey: ['activities', recordId],
    queryFn: ({ pageParam = 0 }) => getActivities(recordId, pageParam, 20),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.activities.length < lastPage.size) {
        return undefined
      }
      return lastPage.page + 1
    },
    enabled: !!recordId && options.enabled !== false,
    staleTime: 30 * 1000,
  })

  const addMutation = useMutation({
    mutationFn: (data: AddActivityRequest) => addActivity(recordId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    },
  })

  const uploadFileMutation = useMutation({
    mutationFn: (request: UploadActivityFileRequest) => uploadActivityFile(recordId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities', recordId] })
    },
  })

  return {
    activities: query.data?.pages.flatMap((page) => page.activities) ?? [],
    activitySessionId: query.data?.pages[0]?.activitySessionId,
    nextActivityAtReminder: query.data?.pages[0]?.nextActivityAtReminder,
    nextActivityInstructions: query.data?.pages[0]?.nextActivityInstructions,
    followUpTagId: query.data?.pages[0]?.followUpTagId,
    followUpTagLabel: query.data?.pages[0]?.followUpTagLabel,
    followUpTagColorHex: query.data?.pages[0]?.followUpTagColorHex,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    addActivity: addMutation.mutate,
    isAdding: addMutation.isPending,
    addError: addMutation.error,
    uploadFileAsync: uploadFileMutation.mutateAsync,
    isUploadingFile: uploadFileMutation.isPending,
    uploadFileError: uploadFileMutation.error,
  }
}